import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subscription, throwError, TimeoutError } from 'rxjs';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DefaultSiteSelectionInfo, SitePermissionInfo, Title } from '../my-hospital-users/my-hospital-user.model';
import { AuthenticationService } from '../authentication/services/authentication.service';
import { SettingsService } from '../services/settings.service';
import { UserSettingsService } from '../services/user-settings.service';
import { SnackbarService } from '../services/snackbar.sevice';
import { catchError, debounceTime, map, mergeMap, shareReplay, switchMap } from 'rxjs/operators';
import { Constants } from '../models/constants';
import { HttpErrorResponse } from '@angular/common/http';
import { CountrySetting } from '../models/countrysetting';
import { PhoneNumberUtil, PhoneNumber } from 'google-libphonenumber';
import { PermissionRoutingService } from '../shared/permission-routing.service';
import { User } from '../models/user';
import {
    PatchUser,
    PatchUserDetails,
    Role,
    RoleEnum,
    UpdateUserOrgRole,
    UpdateUserRolesRequest
} from '../api-module/user-management-api-client-v2';
import { MessageBoxService } from '../messagebox/services/messagebox.service';
import { MessageBoxIcon, MessageBoxButtons, MessageBoxResult } from '../messagebox/messagebox.model';
import { ClientSite } from '../models/clientsite';
import { GetClient, SettingType } from '../api-module/control-centre-api-client';
import { RoleMappingService } from '../services/role-mapping.service';
import { OrgSiteSelectionService } from '../shared/org-site-selection/org-site-selection.service';
import { Urls } from '../models/urls';
import { SettingUtilities } from '@app/shared/setting-utilities';

const phoneUtil = PhoneNumberUtil.getInstance();

@Component({
    selector: 'app-my-details',
    templateUrl: './my-details.component.html',
    styleUrls: ['./my-details.component.scss']
})
export class MyDetailsComponent implements OnInit, OnDestroy {
    private readonly NAME_PATTERN = "[a-zA-ZÀ-ž\u0370-\u03FF\u0400-\u04FF][0-9a-zA-ZÀ-ž\u0370-\u03FF\u0400-\u04FF,.@'-_^ ]*";
    // Phone number related constants
    private readonly PHONE_PATTERN = '[0-9]*';
    private readonly MAX_SIZE_PHONE_EXTENSION = 10;
    private readonly subscriptions = new Subscription();

    roleEnum = RoleEnum;
    roles: Role[];
    userDetailsFormGroup: UntypedFormGroup;
    defaultSiteFormControl: UntypedFormControl;
    defaultCountryCode$: Observable<string>;
    countryCode: string;
    userBehaviorSubject = new BehaviorSubject<User>(null);
    permittedSites = new BehaviorSubject<number[]>([]);
    client$: Observable<GetClient>;
    private readonly orgCode = new BehaviorSubject<string>(null);
    sitesSource = new BehaviorSubject<ClientSite[]>([]);
    triggerSitesRefresh$ = new EventEmitter<void>();
    updatedSitePermission = new BehaviorSubject<SitePermissionInfo[]>([]);
    numSiteSelected$: Observable<number>;
    isSitePermissionChanged = false;
    roleMapping = RoleMappingService;
    saving = false;
    loading = true;
    filteredRoles: Role[] = [];

    Urls = Urls;

    readonly titles: Title[] = [
        { value: 'Dr.', viewValue: 'Dr.' },
        { value: 'Miss', viewValue: 'Miss' },
        { value: 'Ms.', viewValue: 'Ms.' },
        { value: 'Mrs.', viewValue: 'Mrs.' },
        { value: 'Mr.', viewValue: 'Mr.' }
    ];

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly authenticationService: AuthenticationService,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly settingsService: SettingsService,
        private readonly userSettingsService: UserSettingsService,
        private readonly snackbarService: SnackbarService,
        private readonly messageBoxService: MessageBoxService
    ) {}

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get userDetailsForm() {
        return this.userDetailsFormGroup.controls;
    }

    ngOnInit(): void {
        this.initDefaultCountryCode();
        this.initForm();
        this.initSitePermission();
        this.loadUser();
    }

    async submit(): Promise<void> {
        this.markAsTouched(this.userDetailsFormGroup);
        const requestList: Observable<any>[] = [];

        if (!this.userDetailsFormGroup.valid || !this.isRoleAndSiteValid()) {
            this.snackbarService.openSnackBar(Constants.UserCannotBeUpdated, SnackbarService.SNACK_BAR_CLASS_ALERT);
            return;
        }

        if (this.userDetailsFormGroup.dirty) {
            const userDetails = this.getMyDetailsFromForm();

            requestList.push(
                this.userSettingsService.updateUserV2(this.userBehaviorSubject.value.id, userDetails).pipe(catchError(this.getErrorMessage))
            );
        }
        if (this.isSitePermissionChanged) {
            if (
                this.hasSiteSpecificRole(this.userBehaviorSubject.value) &&
                this.selectedSites(this.updatedSitePermission.value).length === 0
            ) {
                const confirmation = await this.confirmChange();
                if (confirmation !== MessageBoxResult.Yes) {
                    this.triggerSitesRefresh$.emit();
                    this.saving = false;
                    return;
                } else {
                    const userRoles = this.getUserRolesFromForm();
                    requestList.push(
                        this.userSettingsService
                            .updateUserOrgRoles(this.userBehaviorSubject.value.id, this.orgCode.value, userRoles)
                            .pipe(catchError(this.getErrorMessage))
                    );
                }
            } else {
                const userRoles = this.getUserRolesFromForm();
                requestList.push(
                    this.userSettingsService
                        .updateUserOrgRoles(this.userBehaviorSubject.value.id, this.orgCode.value, userRoles)
                        .pipe(catchError(this.getErrorMessage))
                );
            }
        }

        if (this.defaultSiteFormControl.dirty && this.defaultSiteFormControl.value !== null) {
            const userDefaultSiteRequest = new PatchUserDetails({
                defaultSiteId: this.defaultSiteFormControl.value
            });

            requestList.push(
                this.userSettingsService
                    .updateUserDefaultSite(this.userBehaviorSubject.value.id, this.orgCode.value, userDefaultSiteRequest)
                    .pipe(catchError(this.getErrorMessage))
            );
        }

        if (requestList.length > 0) {
            this.saving = true;
            const currentOrg = this.orgSiteSelectionService.getOrgSelectedValue();
            if (!currentOrg) {
                try {
                    await this.userSettingsService
                        .createUserOrg(this.userBehaviorSubject.value.id, {
                            orgCode: this.orgCode.value,
                            defaultSiteId: this.defaultSiteFormControl.value === 0 ? null : this.defaultSiteFormControl.value
                        })
                        .toPromise();
                } catch (error) {
                    this.snackbarService.openSnackBar(error, SnackbarService.SNACK_BAR_CLASS_ALERT);
                    this.saving = false;
                }
            }

            let hasError: string;
            for (const req of requestList) {
                try {
                    await req.toPromise();
                } catch (error) {
                    hasError = error;
                }
            }
            this.saving = false;
            if (hasError) {
                this.snackbarService.openSnackBar(hasError, SnackbarService.SNACK_BAR_CLASS_ALERT);
            } else {
                await this.authenticationService.notifyUserUpdated();
                this.snackbarService.openSnackBar(Constants.UserSuccessfullyUpdated, SnackbarService.SNACK_BAR_CLASS_SUCCESS);
                this.clearForms();
            }
        }
    }

    clearForms(): void {
        this.userDetailsFormGroup.markAsPristine();
        this.userDetailsFormGroup.markAsUntouched();
        this.isSitePermissionChanged = false;
        this.defaultSiteFormControl.markAsPristine();
        this.defaultSiteFormControl.markAsUntouched();
    }

    reset(): void {
        this.userBehaviorSubject.next(this.userBehaviorSubject.value);
        this.triggerSitesRefresh$.emit();
        const defaultSite = this.orgSiteSelectionService.getOrgSelectedValue().defaultSiteId;
        this.defaultSiteFormControl.reset(defaultSite ?? 0);
        this.clearForms();
    }

    getErrorMessage(response: HttpErrorResponse): Observable<never> {
        if (response instanceof TimeoutError) {
            console.error('Request timeout: ', JSON.stringify(response));
            return throwError(Constants.UnableToContactServerMessage);
        } else if (
            response instanceof HttpErrorResponse &&
            (response.error instanceof ProgressEvent || response.status === 502 || response.status === 503 || response.status === 504)
        ) {
            console.error('Unable to contact server: ', response.status);
            return throwError(Constants.UnableToContactServerMessage);
        }
        console.error(`Server returned code ${response.status} with body:\n${JSON.stringify(response.error)}`);
        if (response.status === 403) {
            return throwError('You do not have permission to update this user');
        }
        if (response.status === 400) {
            // SPA shouldn't submit an invalid form. if it does and server returns error, it's an error scenario.
            const errorMessage =
                'Something went wrong while updating the user. Please try again in some time. ' +
                'If the problem persists, please contact Everlight support team';
            return throwError(errorMessage);
        }
        if (response.status === 404) {
            const errorMessage =
                'Cannot load the user details. It may have been deleted. Please try again in some time. ' +
                'If the problem persists, please contact Everlight support team';
            return throwError(errorMessage);
        }

        return throwError(Constants.UnableToContactServerMessage);
    }

    private initForm() {
        this.userDetailsFormGroup = this.formBuilder.group({
            title: [null],
            firstName: [null, [Validators.required, Validators.maxLength(64), Validators.pattern(this.NAME_PATTERN)]],
            lastName: [null, [Validators.required, Validators.maxLength(64), Validators.pattern(this.NAME_PATTERN)]],
            emailAddress: [{ value: null, disabled: true }],
            contactPhone: [null, [Validators.pattern(this.PHONE_PATTERN), Validators.maxLength(15)]],
            contactPhoneExt: [null, [Validators.pattern(this.PHONE_PATTERN), Validators.maxLength(this.MAX_SIZE_PHONE_EXTENSION)]]
        });

        this.defaultSiteFormControl = new UntypedFormControl(null);

        const phoneChange$ = this.userDetailsForm.contactPhone.valueChanges as Observable<string>;

        this.subscriptions.add(
            phoneChange$.subscribe(term => {
                this.onPhoneNumberChanged(term, this.countryCode);
            })
        );
    }

    private initDefaultCountryCode() {
        // Pipe to get client observable
        this.client$ = combineLatest([this.userBehaviorSubject.asObservable(), this.orgSiteSelectionService.getOrgSelected$()]).pipe(
            mergeMap(([user, org]) => {
                if (!user) {
                    return of(null as GetClient);
                }
                return this.settingsService.getClientByOrg(org.orgCode);
            })
        );

        // Pipe to get CountrySetting[] observable
        const countrySettings$ = this.settingsService
            .getCountrySettings()
            .pipe(map(response => response.map(item => new CountrySetting(item.country, item.region, item.countryCode))));

        // Pipe to return combined result of countryCode.
        this.defaultCountryCode$ = combineLatest([this.client$, countrySettings$]).pipe(
            map(([client, countrySettings]) => {
                if (!client) {
                    return '';
                }
                return countrySettings.filter(c => c.country === client.countryCode)[0].countryCode;
            })
        );
    }

    private initSitePermission() {
        this.subscriptions.add(
            combineLatest([
                this.orgSiteSelectionService.getAllSites(),
                this.orgSiteSelectionService.getOrgSelected$(),
                this.userSettingsService.getRoles()
            ]).subscribe(([allSites, selectedOrg, roles]) => {
                this.sitesSource.next(allSites);
                this.orgCode.next(selectedOrg.orgCode);
                this.roles = roles;

                this.filteredRoles = this.roles.filter(
                    role =>
                        SettingUtilities.isSettingEnabled(selectedOrg.settings, SettingType.DemandManagement) ||
                        RoleMappingService.getRoleEnum(role.name) !== RoleEnum.DemandManagement
                );

                if (selectedOrg?.roles) {
                    const { allowedSites } = selectedOrg.getAllowedSites();
                    if (!allowedSites) {
                        // if no GeneralAccess, allowedSites will be null
                        this.permittedSites.next([]);
                    } else if (allowedSites.length === 0) {
                        // user has all sites permission
                        this.permittedSites.next([null]);
                    } else {
                        // user has some sites permission
                        this.permittedSites.next([...allowedSites]);
                    }
                }
                this.defaultSiteFormControl.setValue(selectedOrg.defaultSiteId ?? 0);
            })
        );

        this.numSiteSelected$ = this.updatedSitePermission.asObservable().pipe(
            debounceTime(10),
            switchMap(items => of(this.selectedSites(items).length)),
            shareReplay()
        );
    }

    private loadUser() {
        this.subscriptions.add(
            combineLatest([this.authenticationService.currentUser, this.orgSiteSelectionService.getOrgSelected$()]).subscribe(
                ([user, org]) => {
                    this.userBehaviorSubject.next(user);
                }
            )
        );

        this.subscriptions.add(
            combineLatest([this.defaultCountryCode$, this.userBehaviorSubject.asObservable()]).subscribe(([defaultCountryCode, user]) => {
                if (!user) {
                    return;
                }

                this.userDetailsForm.title.setValue(user.title);
                this.userDetailsForm.firstName.setValue(user.firstName);
                this.userDetailsForm.lastName.setValue(user.surname);
                this.userDetailsForm.emailAddress.setValue(user.email);
                if (user.number) {
                    this.countryCode = user.countryCode;
                    this.userDetailsForm.contactPhone.setValue(user.number);
                    this.userDetailsForm.contactPhoneExt.setValue(user.extension);
                } else {
                    this.countryCode = defaultCountryCode;
                    this.userDetailsForm.contactPhone.setValue(null);
                    this.userDetailsForm.contactPhoneExt.setValue(null);
                }
                this.permittedSites.next([...this.permittedSites.value]); // fix the default site dropdown incorrect display for single-site org

                this.loading = false;
            })
        );
    }

    public hasSiteSpecificRole(user: User): boolean {
        return this.hasAnyRoleEnum(user, [
            RoleEnum.GeneralAccess,
            RoleEnum.ClinicalFeedback,
            RoleEnum.ImagingRequestViewer,
            RoleEnum.ImagingRequestEditor
        ]);
    }

    public hasAnyRole(user: User, roles: Role[]): boolean {
        return this.hasAnyRoleEnum(
            user,
            roles.map(role => RoleMappingService.getRoleEnum(role.name))
        );
    }

    public hasAnyRoleEnum(user: User, roleEnums: RoleEnum[]): boolean {
        return roleEnums.some(roleEnum => PermissionRoutingService.checkUserPermission(user, roleEnum, this.orgCode.value));
    }

    private onPhoneNumberChanged(contactNumber: string, countryCode: string) {
        if (!contactNumber) {
            this.userDetailsForm.contactPhoneExt.reset();
            return;
        }
        const phoneNumberNew = countryCode + ' ' + contactNumber;

        let validNumber = false;
        let phoneNumber: PhoneNumber;
        try {
            phoneNumber = phoneUtil.parseAndKeepRawInput(phoneNumberNew, '');
            validNumber = phoneUtil.isValidNumber(phoneNumber);
        } catch (e) {}

        if (validNumber) {
            this.userDetailsForm.contactPhone.setValue(phoneNumber.getNationalNumber().toString(), { emitEvent: false });
        } else {
            this.userDetailsForm.contactPhone.setErrors({ invalidphone: true }, { emitEvent: false });
        }
    }

    private markAsTouched(form: UntypedFormGroup) {
        form.markAllAsTouched();
    }

    private getMyDetailsFromForm(): PatchUser {
        const hasPhoneNumber = !!this.userDetailsForm.contactPhone.value;
        return new PatchUser({
            title: this.userDetailsForm.title.value,
            firstName: this.userDetailsForm.firstName.value,
            surname: this.userDetailsForm.lastName.value,
            phoneNumberCountryCode: hasPhoneNumber ? this.countryCode : null,
            phoneNumber: this.userDetailsForm.contactPhone.value,
            phoneNumberExtension: hasPhoneNumber ? this.userDetailsForm.contactPhoneExt.value : null
        });
    }

    private getUserRolesFromForm(): UpdateUserRolesRequest {
        const result: UpdateUserOrgRole[] = [];
        const user = this.userBehaviorSubject.value;
        this.roles.forEach(role => {
            if (this.hasAnyRole(user, [role])) {
                // if this user has access to this role
                const roleValue = RoleMappingService.getRoleEnum(role.name);
                if (
                    roleValue === RoleEnum.GeneralAccess ||
                    roleValue === RoleEnum.ClinicalFeedback ||
                    roleValue === RoleEnum.ImagingRequestViewer ||
                    roleValue === RoleEnum.ImagingRequestEditor
                ) {
                    const selectedSites = this.selectedSites(this.updatedSitePermission.value);
                    if (selectedSites.length > 0) {
                        // only give user the role if any site selected
                        if (selectedSites.length === this.updatedSitePermission.value?.length) {
                            // all selected
                            result.push(
                                new UpdateUserOrgRole({
                                    roleType: roleValue,
                                    siteId: null
                                })
                            );
                        } else {
                            this.updatedSitePermission.value
                                .filter(info => info.isPermitted)
                                .forEach(info => {
                                    result.push(
                                        new UpdateUserOrgRole({
                                            roleType: roleValue,
                                            siteId: info.siteInfo.siteId
                                        })
                                    );
                                });
                        }
                    }
                } else {
                    result.push(
                        new UpdateUserOrgRole({
                            roleType: roleValue,
                            siteId: null
                        })
                    );
                }
            }
        });

        return new UpdateUserRolesRequest({
            roles: result
        });
    }

    onSitePermissionChanged(sitePermissionList: SitePermissionInfo[]): void {
        this.updatedSitePermission.next(sitePermissionList);
        if (sitePermissionList.length === 1) {
            let nextDefaultSite;
            if (sitePermissionList[0].isPermitted) {
                nextDefaultSite = sitePermissionList[0].siteInfo.siteId;
            } else {
                nextDefaultSite = 0;
            }
            if (this.defaultSiteFormControl.value !== nextDefaultSite) {
                this.defaultSiteFormControl.markAsDirty();
            }
            this.defaultSiteFormControl.setValue(nextDefaultSite);
        } else if (
            sitePermissionList.length > 0 &&
            this.defaultSiteFormControl.value !== null &&
            !sitePermissionList.some(item => item.siteInfo.siteId === this.defaultSiteFormControl.value && item.isPermitted)
        ) {
            if (this.defaultSiteFormControl.value !== 0) {
                this.defaultSiteFormControl.markAsDirty();
            }
            this.defaultSiteFormControl.setValue(0);
        }

        const existingSet = new Set(this.permittedSites.value);
        const selectedArr = this.selectedSites(sitePermissionList);
        this.isSitePermissionChanged =
            sitePermissionList.length > 0 &&
            !(existingSet.size === selectedArr.length && selectedArr.every(item => existingSet.has(item.siteInfo.siteId)));
    }

    onDefaultSiteChanged(siteInfo: DefaultSiteSelectionInfo): void {
        this.defaultSiteFormControl.patchValue(siteInfo.isDefault ? siteInfo.siteId : 0);
        this.defaultSiteFormControl.markAsDirty();
    }

    selectedSites(permissionList: SitePermissionInfo[]): SitePermissionInfo[] {
        return permissionList?.filter(item => item.isPermitted) ?? [];
    }

    isRoleAndSiteValid(): boolean {
        const user = this.userBehaviorSubject.value;
        return !(
            this.hasAnyRoleEnum(user, [RoleEnum.Admin]) &&
            this.hasSiteSpecificRole(user) &&
            this.selectedSites(this.updatedSitePermission.value).length === 0
        );
    }

    private async confirmChange() {
        return await this.messageBoxService
            .showMessageBox({
                title: 'Please Confirm',
                message: 'Do you want to proceed ahead?',
                information: 'You have not selected a site, and will not have Worklist access.',
                informationLine2: 'However, you will still have User Management access.',
                icon: MessageBoxIcon.Confirmation,
                buttons: MessageBoxButtons.YesNo
            })
            .toPromise();
    }
}
